import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { FlaskConical, Layers, Beaker, Sparkles } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SeoHead } from "@/components/SeoHead";
import { staggerContainer, fadeUp } from "@/lib/motion";
import { useContent } from "@/context/ContentContext";

const ITEM_ICONS = [FlaskConical, Layers, Beaker, Sparkles];

export default function Cozumler() {
  const { t } = useTranslation();
  const { media } = useContent();
  const items = t("solutions.items", { returnObjects: true });

  return (
    <div data-testid="cozumler-page" className="py-20 md:py-28">
      <SeoHead pageKey="solutions" />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* HEADER */}
        <Reveal>
          <p className="text-sm tracking-widest uppercase text-[#2b8a7e] mb-5">
            {t("solutions.overline")}
          </p>
          <h1 className="font-display font-bold text-[#1a3636] text-4xl sm:text-5xl lg:text-7xl tracking-tighter leading-none max-w-4xl">
            {t("solutions.title")}
          </h1>
          <p className="mt-6 text-base md:text-lg text-[#1a3636]/65 leading-relaxed max-w-2xl">
            {t("solutions.sub")}
          </p>
        </Reveal>

        {/* IMAGE + FEATURES layout */}
        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Lab image 1 */}
          <Reveal className="lg:col-span-5" delay={0.1}>
            {/* ==== LAB IMAGERY PLACEHOLDER 1 ==== */}
            <div className="relative rounded-2xl overflow-hidden border-4 border-white shadow-[0_20px_40px_rgb(26,54,54,0.12)] h-[420px] lg:h-[560px]">
              <img
                src={media.lab1}
                alt={t("solutions.labLabel")}
                data-testid="solutions-lab-image-0"
                className="w-full h-full object-cover"
              />
              <span className="absolute bottom-4 left-4 bg-[#f8faf9]/90 backdrop-blur px-4 py-1.5 rounded-full text-xs uppercase tracking-widest text-[#1a3636]">
                {t("solutions.labLabel")} 01
              </span>
            </div>
          </Reveal>

          {/* Features list */}
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-60px" }}
            className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6"
          >
            {items.map((item, i) => {
              const Icon = ITEM_ICONS[i];
              return (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  data-testid={`solution-item-${i}`}
                  className="group bg-white rounded-2xl p-8 border border-[#1a3636]/5 shadow-[0_8px_30px_rgb(26,54,54,0.06)] hover:shadow-[0_20px_40px_rgb(43,138,126,0.12)] transition-all duration-500"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#2b8a7e]/10 flex items-center justify-center text-[#2b8a7e] mb-6 group-hover:bg-[#2b8a7e] group-hover:text-white transition-colors duration-500">
                    <Icon size={24} strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display text-xl font-semibold text-[#1a3636] tracking-tight mb-2">
                    {item.name}
                  </h3>
                  <p className="text-[#1a3636]/60 leading-relaxed text-sm">{item.desc}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>

        {/* Lab image 2 - full width */}
        <Reveal className="mt-8">
          {/* ==== LAB IMAGERY PLACEHOLDER 2 ==== */}
          <div className="relative rounded-2xl overflow-hidden border-4 border-white shadow-[0_20px_40px_rgb(26,54,54,0.12)] h-[320px] md:h-[440px]">
            <img
              src={media.lab2}
              alt={t("solutions.labLabel")}
              data-testid="solutions-lab-image-1"
              className="w-full h-full object-cover"
            />
            <span className="absolute bottom-4 left-4 bg-[#f8faf9]/90 backdrop-blur px-4 py-1.5 rounded-full text-xs uppercase tracking-widest text-[#1a3636]">
              {t("solutions.labLabel")} 02
            </span>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
